// Corrections of recorded PRA values (Stage 2 slice 9): the field definitions correctionDialog.vue
// and correctionNote.vue use for a PRA case (PRA_FIELDSET), the live date limits, the History
// lines of pra.corrected and the error keys. Plain module with no Vue, so it runs under
// `node --test`. Labels are i18n keys; the API checks everything again and decides.

import { todayRome } from "./draftForm.mjs";
import { formatCivilDate } from "./deadlines.mjs";
import { AGENCY_NAME_MAX, REQUEST_CHANNELS } from "./praRequestForm.mjs";

// Same limit as the API (pra/manualConnector.js NUMBER_MAX) for the CRD and receipt numbers.
export const NUMBER_MAX = 100;

// group -> the field paths the dialog shows (API pra/corrections.js GROUPS). Files are not
// corrected here: they change through "Sostituisci" (decision Q3).
const GROUPS = {
  "crd.number": ["crd.number"],
  "crd.issuedOn": ["crd.issuedOn"],
  "request.date": ["request.date"],
  "request.channel": ["request.channel", "request.agencyName"],
  "receipt.number": ["receipt.number"],
  "receipt.date": ["receipt.date"],
};

export const PRA_GROUPS = Object.keys(GROUPS);

// The legal order of the case dates (API pra/rules.js DATE_ORDER). Equal dates are allowed.
const DATE_ORDER = [
  "arrival.date",
  "crd.issuedOn",
  "request.date",
  "receipt.date",
];

// How the dialog, "Valore precedente" and History draw each field. The last two are only logged
// (a request.date correction recomputes them).
const FIELD_SPECS = {
  "crd.number": {
    type: "text",
    label: "acceptance.pra.crd.number",
    max: NUMBER_MAX,
    required: true,
  },
  "crd.issuedOn": { type: "date", label: "acceptance.pra.crd.issued_on" },
  "request.date": { type: "date", label: "acceptance.pra.request.date" },
  "request.channel": {
    type: "select",
    label: "acceptance.pra.request.channel",
    options: REQUEST_CHANNELS,
    optionKey: "acceptance.pra.request.channels",
    required: true,
  },
  "request.agencyName": {
    type: "text",
    label: "acceptance.pra.request.agency_name",
    max: AGENCY_NAME_MAX,
  },
  "receipt.number": {
    type: "text",
    label: "acceptance.pra.receipt.number",
    max: NUMBER_MAX,
    required: true,
  },
  "receipt.date": { type: "date", label: "acceptance.pra.receipt.date" },
  "request.lateAtRecording": {
    label: "acceptance.pra.corrections.fields.late_at_recording",
    flag: true,
  },
  "request.dueDateAtRecording": {
    label: "acceptance.pra.corrections.fields.due_date_at_recording",
    date: true,
  },
};

// The arrival date only appears as the other side of a date_order error.
const DATE_LABELS = {
  "arrival.date": "acceptance.arrival.date",
  "crd.issuedOn": "acceptance.pra.crd.issued_on",
  "request.date": "acceptance.pra.request.date",
  "receipt.date": "acceptance.pra.receipt.date",
};

export function praGroupFields(group) {
  return Object.prototype.hasOwnProperty.call(GROUPS, group)
    ? GROUPS[group]
    : [];
}

// The dialog title names the group by its first field ("Correggi: Numero CRD").
export function praGroupLabelKey(group) {
  const [path] = praGroupFields(group);
  return path ? praFieldSpec(path).label : "";
}

export function praFieldSpec(path) {
  if (Object.prototype.hasOwnProperty.call(FIELD_SPECS, path)) {
    return FIELD_SPECS[path];
  }
  return { type: "text", label: path, max: NUMBER_MAX };
}

const isDatePath = (path) =>
  praFieldSpec(path).type === "date" || praFieldSpec(path).date === true;

// The case's recorded values, flat and keyed by path (the dialog's `values`).
export function praValues(praCase) {
  const values = {};
  if (!praCase) return values;
  PRA_GROUPS.forEach((group) => {
    praGroupFields(group).forEach((path) => {
      const [step, key] = path.split(".");
      const recorded = praCase[step];
      values[path] =
        recorded && recorded[key] !== undefined ? recorded[key] : null;
    });
  });
  return values;
}

// Whether the group's step is recorded, so it has something to correct.
export function praCorrectable(praCase, group) {
  const fields = praGroupFields(group);
  if (!praCase || !fields.length) return false;
  return Boolean(praCase[fields[0].split(".")[0]]);
}

// How to show a stored value: { empty: true }, { key } (an i18n key) or { text }.
export function praValueDisplay(path, value) {
  const spec = praFieldSpec(path);
  if (spec.flag) return value === true ? { text: "✓" } : { empty: true };
  if (value === null || value === undefined || value === "") {
    return { empty: true };
  }
  if (isDatePath(path)) return { text: formatCivilDate(value) };
  if (spec.optionKey) return { key: `${spec.optionKey}.${value}` };
  return { text: String(value) };
}

// The agency name only shows for an STA agency (the API drops it for the other channels).
export function praFieldVisible(path, form) {
  if (path !== "request.agencyName") return true;
  return (form && form["request.channel"]) === "sta_agency";
}

const text = (value) =>
  value === null || value === undefined ? "" : String(value).trim();

// A field's value as the API stores it: trimmed text, null when blank; the agency name is null
// unless the channel is sta_agency.
function normalised(path, form) {
  if (!praFieldVisible(path, form)) return null;
  return text(form[path]) || null;
}

// What the request sends as `value`: one value, or { channel, agencyName } for the channel.
export function praCorrectionValue(group, form) {
  if (group === "request.channel") {
    const value = { channel: normalised("request.channel", form) };
    if (value.channel === "sta_agency") {
      value.agencyName = normalised("request.agencyName", form);
    }
    return value;
  }
  const [path] = praGroupFields(group);
  return path ? normalised(path, form) : null;
}

// Fields of the group whose value differs from the case's (empty: 422 no_change).
export function praChangedFields(group, form, saved) {
  return praGroupFields(group).filter(
    (path) => normalised(path, form) !== (text(saved && saved[path]) || null),
  );
}

// The neighbouring recorded dates limit a date: arrival <= CRD <= request <= receipt, and none
// after today in Europe/Rome. -> { min, max } ('' when there is no lower limit), or null for a
// path that is not a case date.
export function praDateLimits(
  path,
  praCase,
  { arrivalDate, now = new Date() },
) {
  const index = DATE_ORDER.indexOf(path);
  if (index < 1) return null;
  const values = { ...praValues(praCase), "arrival.date": arrivalDate || null };
  const earlier = DATE_ORDER.slice(0, index)
    .map((key) => values[key])
    .filter(Boolean);
  const later = DATE_ORDER.slice(index + 1)
    .map((key) => values[key])
    .find(Boolean);
  const today = todayRome(now);
  return {
    min: earlier.length ? earlier[earlier.length - 1] : "",
    max: later && later < today ? later : today,
  };
}

// What stops the preview and the save: null, "required" (only disables), "date_range",
// "too_long" or "agency_required". `limits` maps a date path to praDateLimits.
export function praInputProblem(group, form, limits = {}) {
  let problem = null;
  praGroupFields(group).forEach((path) => {
    if (problem || !praFieldVisible(path, form)) return;
    const spec = praFieldSpec(path);
    const value = text(form[path]);
    if (spec.type === "date") {
      const range = limits[path] || {};
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) problem = "required";
      else if (
        (range.min && value < range.min) ||
        (range.max && value > range.max)
      ) {
        problem = "date_range";
      }
    } else if (path === "request.agencyName") {
      if (!value) problem = "agency_required";
      else if (value.length > spec.max) problem = "too_long";
    } else if (!value) {
      if (spec.required) problem = "required";
    } else if (spec.max && value.length > spec.max) {
      problem = "too_long";
    }
  });
  return problem;
}

// Corrections of the group's fields, as [{ path, from, correction }], for "Valore precedente".
export function praPreviousValues(corrections, group) {
  const all = corrections || {};
  return praGroupFields(group)
    .filter((path) => all[path])
    .map((path) => ({ path, from: all[path].from, correction: all[path] }));
}

// One pra.corrected change { field, from, to } -> { label, lineCode, from, to } for History (the
// shape of corrections.mjs changeLine), or null for a field the PRA card does not know.
export function praChangeLine(change) {
  const path = String((change && change.field) || "");
  if (!Object.prototype.hasOwnProperty.call(FIELD_SPECS, path)) return null;
  return {
    label: FIELD_SPECS[path].label,
    lineCode: null,
    from: praValueDisplay(path, change.from),
    to: praValueDisplay(path, change.to),
  };
}

// dryRun `changes` -> [{ field, label, from, to }] for the preview table.
export function praPreviewRows(data) {
  return ((data && data.changes) || [])
    .map((change) => {
      const line = praChangeLine(change);
      return line ? { field: change.field, ...line } : null;
    })
    .filter(Boolean);
}

// The late warning of a request.date dryRun: the due date as DD/MM/YYYY, or null.
export function praLateWarning(data) {
  const warning = ((data && data.warnings) || []).find(
    (item) => item.code === "request_late",
  );
  return warning ? { dueDate: formatCivilDate(warning.dueDate) } : null;
}

// A failed correction -> { key, other } (an i18n key, and for date_order the label key of the
// conflicting date), or null to show the API's errorMsg.
const ERROR_KEYS = {
  not_manager: "acceptance.pra.corrections.errors.not_manager",
  not_correctable: "acceptance.corrections.errors.not_correctable",
  reason_required: "acceptance.corrections.errors.reason_required",
  date_in_future: "acceptance.pra.corrections.errors.date_in_future",
  crd_number_in_use: "acceptance.pra.crd.errors.crd_number_in_use",
};

export function praCorrectionErrorKey(err) {
  const data = (err && err.response && err.response.data) || {};
  const details = data.details || {};
  if (data.code === "date_order") {
    return {
      key: "acceptance.pra.corrections.errors.date_order",
      other: DATE_LABELS[details.conflictsWith] || null,
    };
  }
  if (data.code === "invalid_input") {
    if (details.reason === "no_change") {
      return { key: "acceptance.corrections.errors.no_change", other: null };
    }
    if (details.field === "request.agencyName") {
      return {
        key: "acceptance.pra.request.errors.agency_required",
        other: null,
      };
    }
    return null;
  }
  return ERROR_KEYS[data.code]
    ? { key: ERROR_KEYS[data.code], other: null }
    : null;
}

// One commandId per correction: a retry of the same input reuses it, so it is recorded once.
export function praCorrectionSubmitKey({ group, value, reason }) {
  return JSON.stringify({ group, value, reason });
}

// The helpers correctionDialog.vue and correctionNote.vue use for a PRA case instead of the
// Stage 1 ones (frozen: Vue keeps it as is).
export const PRA_FIELDSET = Object.freeze({
  groupFields: praGroupFields,
  groupLabelKey: praGroupLabelKey,
  fieldSpec: praFieldSpec,
  valueDisplay: praValueDisplay,
  visible: praFieldVisible,
  changedFields: praChangedFields,
  correctionValue: praCorrectionValue,
  previousValues: praPreviousValues,
  inputProblem: praInputProblem,
  previewRows: praPreviewRows,
  lateWarning: praLateWarning,
});
